import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DeliveryDashboard, LinkedDelivery } from "./components/LinkedDelivery";
import {
  ArrowLeft,
  AlertTriangle,
  Bell,
  Calculator,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  GitBranch,
  LockKeyhole,
  MessageSquare,
  Printer,
  Save,
  Send,
  ShieldCheck,
  Users,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useApi } from "./api";
import { usePermissions } from "./permissions";
import { clearIntakeReviewFields } from "./intake-governance";
import { intakeDepartment } from "./intake-department";
import { RolloutControls } from "./components/RolloutControls";
import { InitiativeTable } from "./components/InitiativeTable";
import { PortfolioReview } from "./components/PortfolioReview";
import { GovernanceGuide } from "./components/GovernanceGuide";
import { DependencyNotes } from "./components/DependencyNotes";
import {
  ButtonLoader,
  Card,
  EmptyState,
  ErrorNotice,
  FieldError,
  Kpi,
  Loading,
  PageHeader,
  PageError,
  PageSkeleton,
  SkeletonCard,
  SuccessNotice,
} from "./components/Ui";
import type { AccessGrant, DashboardSummary, Initiative, User } from "./types";
import { BusinessRoles } from "./components/BusinessRoles";
import { NativeCapacitySummary } from "./components/NativeCapacity";
import { businessRoleLabels, type BusinessPolicy } from "@payangel/shared";
import {
  compareQuarters,
  downloadCsv,
  formatDate,
  label,
  longestDependencyChain,
  normalizeQuarter,
} from "./utils";
import { validateIntake, type IntakeTextField } from "./validation";

function InitiateButton() {
  const permissions = usePermissions();
  return permissions.can("initiatives.create") ? (
    <Link className="btn-primary" to="/intake">
      New initiative
    </Link>
  ) : null;
}
function GovernanceOnly({
  children,
  manage = false,
}: {
  children: React.ReactNode;
  manage?: boolean;
}) {
  const permissions = usePermissions();
  return permissions.can(manage ? "governance.manage" : "governance.view") ? (
    <>{children}</>
  ) : null;
}
function SensitiveExports({ children }: { children: React.ReactNode }) {
  const permissions = usePermissions();
  return permissions.can("executive.view") && permissions.businessCan("export", true) ? <>{children}</> : null;
}
function useInitiatives() {
  const api = useApi();
  return useQuery({
    queryKey: ["initiatives"],
    queryFn: () =>
      api.get<{ items: Initiative[]; total: number }>("/initiatives"),
  });
}

export function DashboardPage() {
  const permissions = usePermissions();
  const api = useApi();
  const summary = useQuery({
    queryKey: ["dashboard-summary"],
    queryFn: () => api.get<DashboardSummary>("/dashboard/summary"),
  });
  const initiatives = useInitiatives();
  if (summary.isPending || initiatives.isPending) return <PageSkeleton />;
  if (summary.isError)
    return (
      <PageError
        error={summary.error}
        onRetry={() => void summary.refetch()}
        retrying={summary.isFetching}
      />
    );
  if (initiatives.isError)
    return (
      <PageError
        error={initiatives.error}
        onRetry={() => void initiatives.refetch()}
        retrying={initiatives.isFetching}
      />
    );
  const highPriority = initiatives.data.items
    .filter(
      (item) =>
        item.priorityBand === "critical" || item.priorityBand === "high",
    )
    .slice(0, 8);
  return (
    <div className="page scroll-page">
      <PageHeader
        title="Portfolio dashboard"
        description="Live prioritisation, freshness, delivery, and governance position."
        actions={<InitiateButton />}
      />
      <div className="kpi-bar">
        <Kpi
          label="Portfolio"
          value={summary.data.total}
          detail="visible initiatives"
        />
        <Kpi
          label="Approved"
          value={summary.data.decisions.approved ?? 0}
          tone="green"
        />
        <Kpi
          label="In delivery"
          value={summary.data.delivery.in_delivery ?? 0}
          tone="blue"
        />
        {permissions.can("governance.view") && (
          <Kpi
            label="Board items"
            value={summary.data.boardRequired}
            tone="purple"
          />
        )}
        <Kpi label="Fresh" value={summary.data.freshness.fresh} tone="green" />
        <Kpi label="Watch" value={summary.data.freshness.watch} tone="amber" />
        <Kpi label="Stale" value={summary.data.freshness.stale} tone="red" />
      </div>
      <div className="content-grid dashboard-grid">
        <NativeCapacitySummary />
        <DeliveryDashboard />
        <Card className="span-2">
          <div className="card-header">
            <div>
              <h2>Priority portfolio</h2>
              <p>
                Highest adjusted RICE scores, with explicit overrides respected.
              </p>
            </div>
            <Link to="/initiatives">View all</Link>
          </div>
          <InitiativeTable items={highPriority} />
        </Card>
        <Card>
          <div className="card-header">
            <div>
              <h2>Update Status</h2>
              <p>Days since the last governance review.</p>
            </div>
          </div>
          <DonutLike
            rows={[
              ["Fresh", summary.data.freshness.fresh, "green"],
              ["Watch", summary.data.freshness.watch, "amber"],
              ["Stale", summary.data.freshness.stale, "red"],
              ["Not reviewed", summary.data.freshness.notReviewed, "muted"],
            ]}
            total={summary.data.total}
          />
        </Card>
        <Card>
          <div className="card-header">
            <div>
              <h2>Category mix</h2>
              <p>Strategic portfolio allocation.</p>
            </div>
          </div>
          <div className="metric-list">
            {Object.entries(summary.data.categories).map(([key, value]) => (
              <div className="metric-row" key={key}>
                <span>
                  <span className={`cat-badge cat-${key.toLowerCase()}`}>
                    {key}
                  </span>{" "}
                  {categoryName(key)}
                </span>
                <strong>{value}</strong>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

export function InitiativesPage({
  productView = false,
}: {
  productView?: boolean;
}) {
  const api = useApi();
  const query = useInitiatives();
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [freshness, setFreshness] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  useEffect(() => setPage(1), [search, category, freshness, pageSize]);
  if (query.isPending) return <PageSkeleton variant="table" />;
  if (query.isError)
    return (
      <PageError
        error={query.error}
        onRetry={() => void query.refetch()}
        retrying={query.isFetching}
      />
    );
  const items = query.data.items.filter((item) => {
    const haystack =
      `${item.publicId} ${item.name} ${item.description} ${item.team}`.toLowerCase();
    return (
      (!search || haystack.includes(search.toLowerCase())) &&
      (category === "all" || item.category === category) &&
      (freshness === "all" || item.freshness.status === freshness)
    );
  });
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const visibleItems = items.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );
  return (
    <div className="page scroll-page">
      <PageHeader
        title={productView ? "Product register" : "Initiative register"}
        description={`${query.data.total} initiatives in your accessible portfolio · ${items.length} match your filters${productView ? " · The same portfolio records, including product and enabling work; no separate inventory." : ""}`}
        actions={
          <>
            <InitiateButton />
            <SensitiveExports>
              <ExportButton
                label="CSV"
                icon={<Download size={14} />}
                run={() =>
                  api.download(
                    "/reports/initiatives.csv",
                    "strategic-initiatives.csv",
                  )
                }
              />
              <ExportButton
                label="Excel"
                icon={<FileSpreadsheet size={14} />}
                run={() =>
                  api.download(
                    "/reports/initiatives.xlsx",
                    "strategic-initiatives.xlsx",
                  )
                }
              />
              <ExportButton
                label="PDF"
                icon={<Printer size={14} />}
                run={() =>
                  api.download(
                    "/reports/executive.pdf",
                    "strategic-executive-report.pdf",
                  )
                }
              />
            </SensitiveExports>
          </>
        }
      />
      <div className="toolbar" role="search">
        <label className="search-wrap">
          <span className="sr-only">Search initiatives</span>
          <input
            className="search-input"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search ID, initiative, team…"
          />
        </label>
        <label>
          <span className="sr-only">Category</span>
          <select
            value={category}
            onChange={(event) => setCategory(event.target.value)}
          >
            <option value="all">All categories</option>
            {["A", "B", "C", "D", "F"].map((value) => (
              <option key={value}>{value}</option>
            ))}
          </select>
        </label>
        <label>
          <span className="sr-only">Freshness</span>
          <select
            value={freshness}
            onChange={(event) => setFreshness(event.target.value)}
          >
            <option value="all">All freshness</option>
            <option value="fresh">Fresh</option>
            <option value="watch">Watch</option>
            <option value="stale">Stale</option>
          </select>
        </label>
      </div>
      <Card className="table-card">
        <InitiativeTable items={visibleItems} />
        <div className="register-pagination" aria-label="Initiative pages">
          <label>
            Rows per page{" "}
            <select
              value={pageSize}
              onChange={(event) => setPageSize(Number(event.target.value))}
            >
              {[25, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
          <span>
            Page {currentPage} of {totalPages} · {items.length} results
          </span>
          <button
            className="btn-outline"
            disabled={currentPage <= 1}
            onClick={() => setPage(currentPage - 1)}
          >
            Previous
          </button>
          <button
            className="btn-outline"
            disabled={currentPage >= totalPages}
            onClick={() => setPage(currentPage + 1)}
          >
            Next
          </button>
        </div>
      </Card>
    </div>
  );
}

export function InitiativeDetailPage({ user }: { user: User }) {
  const permissions = usePermissions();
  const { id = "" } = useParams();
  const api = useApi();
  const client = useQueryClient();
  const [comment, setComment] = useState("");
  const [commentTouched, setCommentTouched] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const query = useQuery({
    queryKey: ["initiative", id],
    queryFn: () => api.get<Initiative>(`/initiatives/${id}`),
  });
  const comments = useQuery({
    queryKey: ["comments", id],
    queryFn: () =>
      api.get<{
        items: Array<{ id: number; body: string; createdAt: string }>;
      }>(`/initiatives/${id}/comments`),
  });
  const transition = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post<Initiative>(`/initiatives/${id}/transition`, body),
    onSuccess: (data) => {
      setFeedback("Lifecycle transition applied and added to the audit trail.");
      client.setQueryData(["initiative", id], data);
      void client.invalidateQueries({ queryKey: ["initiatives"] });
      void client.invalidateQueries({ queryKey: ["dashboard-summary"] });
    },
  });
  const addComment = useMutation({
    mutationFn: () =>
      api.post(`/initiatives/${id}/comments`, { body: comment }),
    onSuccess: () => {
      setComment("");
      setCommentTouched(false);
      void comments.refetch();
    },
  });
  const update = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.patch<Initiative>(`/initiatives/${id}`, body),
    onSuccess: (data, variables) => {
      setFeedback(
        "score" in variables
          ? "RICE score recalculated and saved."
          : "Initiative details saved successfully.",
      );
      client.setQueryData(["initiative", id], data);
      void client.invalidateQueries({ queryKey: ["initiatives"] });
      void client.invalidateQueries({ queryKey: ["dashboard-summary"] });
    },
  });
  useEffect(() => {
    if (!feedback) return;
    const timer = window.setTimeout(() => setFeedback(null), 5000);
    return () => window.clearTimeout(timer);
  }, [feedback]);
  if (query.isPending) return <PageSkeleton variant="detail" />;
  if (query.isError)
    return (
      <PageError
        error={query.error}
        onRetry={() => void query.refetch()}
        retrying={query.isFetching}
      />
    );
  const item = query.data;
  return (
    <div className="page scroll-page detail-page">
      <PageHeader
        title={item.name}
        description={`${item.publicId} · ${item.team} · Version ${item.version}`}
        actions={
          <Link className="btn-outline" to="/initiatives">
            <ArrowLeft size={14} /> Register
          </Link>
        }
      />
      {feedback && (
        <div className="page-feedback">
          <SuccessNotice>{feedback}</SuccessNotice>
        </div>
      )}
      <div className="detail-grid">
        <aside>
          {permissions.can("capacity.view") && (
            <Card>
              <h2>Linked delivery</h2>
              <LinkedDelivery initiativeId={item.id} editable />
            </Card>
          )}
          <Card>
            <div className="badge-row">
              <span className={`cat-badge cat-${item.category.toLowerCase()}`}>
                Category {item.category}
              </span>
              <span className={`status-badge status-${item.workflowState}`}>
                {label(item.workflowState)}
              </span>
              {item.confidentiality === "confidential" && (
                <span className="board-badge">
                  <LockKeyhole size={12} /> Confidential
                </span>
              )}
            </div>
            {permissions.businessCan("score.view", true) && <ScoreCard item={item} />}
            <dl className="definition-list">
              <div>
                <dt>Decision</dt>
                <dd>{label(item.decisionOutcome)}</dd>
              </div>
              <div>
                <dt>Delivery</dt>
                <dd>{label(item.deliveryState)}</dd>
              </div>
              <GovernanceOnly>
                <div>
                  <dt>Board lifecycle</dt>
                  <dd>{label(item.boardState)}</dd>
                </div>
              </GovernanceOnly>
              <div>
                <dt>Freshness</dt>
                <dd>
                  {item.freshness.days == null
                    ? "Not reviewed"
                    : `${label(item.freshness.status)} · ${item.freshness.days} days`}
                </dd>
              </div>
              <div>
                <dt>Target</dt>
                <dd>{item.targetQuarter ?? "—"}</dd>
              </div>
            </dl>
          </Card>
          {(permissions.businessCan("decide", user.role === "admin") || permissions.businessCan("delivery.update", user.role === "admin") || permissions.can("governance.manage") || (item.workflowState === "draft" && permissions.businessCan("submit"))) && (
            <LifecycleForm
              administrator={user.role === "admin"}
              item={item}
              pending={transition.isPending}
              error={transition.error}
              onSubmit={(body) => transition.mutate(body)}
            />
          )}
          {permissions.businessCan("score.edit", user.role === "admin") && (
            <ScoringForm
              item={item}
              pending={update.isPending}
              error={update.error}
              onSubmit={(body) => update.mutate(body)}
            />
          )}
        </aside>
        <div className="detail-content">
          {permissions.can("governance.manage") && (
            <ReviewerChecks
              key={`review-${item.version}`}
              item={item}
              pending={update.isPending}
              error={update.error}
              onSubmit={(body) => update.mutate(body)}
            />
          )}
          <Card>
            <div className="card-header">
              <div>
                <h2>Strategic case</h2>
                <p>{item.strategicDriver}</p>
              </div>
            </div>
            <p className="prose">{item.description}</p>
            <h3>Expected outcome</h3>
            <p className="prose">
              {item.expectedOutcome || "Not yet recorded"}
            </p>
          </Card>
          {permissions.businessCan("edit", user.role === "admin" ||
            (item.createdBy === user.id && item.workflowState === "draft")) && (
            <InitiativeEditForm
              item={item}
              pending={update.isPending}
              error={update.error}
              onSubmit={(body) => update.mutate(body)}
            />
          )}
          <Card>
            <div className="card-header">
              <div>
                <h2>Ownership and delivery</h2>
              </div>
            </div>
            <dl className="definition-grid">
              <div>
                <dt>Sponsor</dt>
                <dd>{item.sponsor}</dd>
              </div>
              <div>
                <dt>Product owner</dt>
                <dd>{item.productOwner ?? "Unassigned"}</dd>
              </div>
              <div>
                <dt>Technical owner</dt>
                <dd>{item.technicalOwner ?? "Unassigned"}</dd>
              </div>
              <div>
                <dt>Estimated effort</dt>
                <dd>
                  {item.estimatedSprints == null
                    ? "—"
                    : `${item.estimatedSprints} sprints`}
                </dd>
              </div>
              <div>
                <dt>Deadline</dt>
                <dd>{item.deadline ?? "—"}</dd>
              </div>
              <div>
                <dt>Last reviewed</dt>
                <dd>{formatDate(item.lastReviewedAt)}</dd>
              </div>
            </dl>
          </Card>
          <Card>
            <div className="card-header">
              <div>
                <h2>Dependencies</h2>
                <p>Validated upstream initiative references.</p>
              </div>
              <Link to="/dependencies">
                <GitBranch size={14} /> View map
              </Link>
            </div>
            <div className="chip-row">
              {item.dependencyIds?.length ? (
                item.dependencyIds.map((dependency) => (
                  <Link
                    className="dep-chip upstream"
                    key={dependency}
                    to={`/initiatives/SPD-PA-${String(dependency).padStart(3, "0")}`}
                  >
                    SPD-PA-{String(dependency).padStart(3, "0")}
                  </Link>
                ))
              ) : (
                <span className="muted">No upstream dependencies</span>
              )}
            </div>
            <DependencyNotes item={item} editable={user.role === "admin"} />
          </Card>
          <AttachmentSection initiative={item} />
          {user.role === "admin" && <ParticipantSection initiative={item} />}
          <Card>
            <div className="card-header">
              <div>
                <h2>
                  <MessageSquare size={16} /> Discussion
                </h2>
                <p>Comments are attributed and audited.</p>
              </div>
            </div>
            {comments.isPending ? (
              <Loading compact label="Loading discussion…" />
            ) : comments.isError ? (
              <ErrorNotice error={comments.error} />
            ) : comments.data.items.length === 0 ? (
              <EmptyState
                title="No comments yet"
                description="Start the governance discussion for this initiative."
              />
            ) : (
              comments.data.items.map((entry) => (
                <div className="comment" key={entry.id}>
                  <p>{entry.body}</p>
                  <small>{formatDate(entry.createdAt)}</small>
                </div>
              ))
            )}
            {permissions.businessCan("edit",true) && <form
              className="comment-form"
              onSubmit={(event) => {
                event.preventDefault();
                if (comment.trim()) addComment.mutate();
              }}
            >
              <label>
                <span className="sr-only">Add comment</span>
                <textarea
                  value={comment}
                  onChange={(event) => setComment(event.target.value)}
                  onBlur={() => setCommentTouched(true)}
                  placeholder="Add a comment…"
                  rows={3}
                  minLength={1}
                  maxLength={10000}
                  aria-invalid={commentTouched && !comment.trim()}
                />
                <span className="comment-meta">
                  <FieldError>
                    {commentTouched && !comment.trim()
                      ? "Enter a comment before posting."
                      : undefined}
                  </FieldError>
                  <small>{comment.length}/10,000</small>
                </span>
              </label>
              {addComment.error && <ErrorNotice error={addComment.error} />}
              <button
                className="btn-primary"
                disabled={!comment.trim() || addComment.isPending}
              >
                {addComment.isPending ? (
                  <ButtonLoader label="Posting…" />
                ) : (
                  "Add comment"
                )}
              </button>
            </form>}
          </Card>
        </div>
      </div>
    </div>
  );
}

export function NotificationsPage() {
  const api = useApi();
  const client = useQueryClient();
  const query = useQuery({
    queryKey: ["notifications"],
    queryFn: () =>
      api.get<{
        unread: number;
        items: Array<{
          id: number;
          title: string;
          body: string;
          entityId: string | null;
          readAt: string | null;
          createdAt: string;
        }>;
      }>("/notifications"),
  });
  const markRead = useMutation({
    mutationFn: (notificationId: number) =>
      api.patch(`/notifications/${notificationId}/read`, {}),
    onSuccess: () =>
      void client.invalidateQueries({ queryKey: ["notifications"] }),
  });
  if (query.isPending) return <PageSkeleton variant="table" />;
  if (query.isError)
    return (
      <PageError
        error={query.error}
        onRetry={() => void query.refetch()}
        retrying={query.isFetching}
      />
    );
  return (
    <div className="page scroll-page">
      <PageHeader
        title="Notifications"
        description={`${query.data.unread} unread governance updates`}
      />
      <Card>
        <div className="notification-list">
          {query.data.items.length === 0 ? (
            <EmptyState
              title="You are all caught up"
              description="Lifecycle decisions and governance updates will appear here."
            />
          ) : (
            query.data.items.map((item) => (
              <div
                className={`notification-item ${item.readAt ? "read" : "unread"}`}
                key={item.id}
              >
                <Bell size={17} />
                <div>
                  <strong>{item.title}</strong>
                  <p>{item.body}</p>
                  <small>{formatDate(item.createdAt)}</small>
                </div>
                <div>
                  {item.entityId && (
                    <Link
                      className="btn-outline"
                      to={`/initiatives/${item.entityId}`}
                    >
                      Open
                    </Link>
                  )}
                  {!item.readAt && (
                    <button
                      className="btn-outline"
                      disabled={markRead.isPending}
                      onClick={() => markRead.mutate(item.id)}
                    >
                      {markRead.isPending && markRead.variables === item.id ? (
                        <ButtonLoader label="Updating…" />
                      ) : (
                        "Mark read"
                      )}
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
        {markRead.error && <ErrorNotice error={markRead.error} />}
      </Card>
    </div>
  );
}

type IntakeState = {
  name: string;
  description: string;
  expectedOutcome: string;
  category: "A" | "B" | "C" | "D" | "F";
  strategicDriver: string;
  team: string;
  sponsor: string;
  productOwner: string;
  technicalOwner: string;
  targetQuarter: string;
  deadline: string;
  estimatedSprints: string;
  confidentiality: "standard" | "confidential";
  regulatoryApprovalRequired: boolean;
  materialCapitalCommitment: boolean;
  contractualExclusivity: boolean;
  materialEnterpriseRisk: boolean;
};
const emptyIntake: IntakeState = {
  name: "",
  description: "",
  expectedOutcome: "",
  category: "C",
  strategicDriver: "",
  team: "",
  sponsor: "",
  productOwner: "",
  technicalOwner: "",
  targetQuarter: "",
  deadline: "",
  estimatedSprints: "",
  confidentiality: "standard",
  regulatoryApprovalRequired: false,
  materialCapitalCommitment: false,
  contractualExclusivity: false,
  materialEnterpriseRisk: false,
};

export function IntakePage() {
  const api = useApi();
  const permissions = usePermissions();
  const permissionsReady = !permissions.isPending && !permissions.isError;
  const departmentScoped = permissions.businessAccess?.scope === "department";
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  const [storedForm, setForm] = useState<IntakeState>(() => {
    const saved = localStorage.getItem("strategic-intake-draft");
    if (!saved) return emptyIntake;
    try {
      return clearIntakeReviewFields({ ...emptyIntake, ...(JSON.parse(saved) as Partial<IntakeState>) });
    } catch {
      localStorage.removeItem("strategic-intake-draft");
      return emptyIntake;
    }
  });
  const form = useMemo(() => ({
    ...storedForm,
    team: intakeDepartment(storedForm.team, permissions.businessAccess),
  }), [storedForm, permissions.businessAccess]);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [draftHydrated, setDraftHydrated] = useState(false);
  const [draftStatus, setDraftStatus] = useState<"idle" | "saving" | "error">(
    "idle",
  );
  const [touched, setTouched] = useState<
    Partial<Record<IntakeTextField, boolean>>
  >({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const serverDraft = useQuery({
    queryKey: ["initiative-draft"],
    queryFn: () =>
      api.get<{
        draft: { payload: Partial<IntakeState>; updatedAt: string } | null;
      }>("/initiatives/draft"),
  });
  const mutation = useMutation({
    mutationFn: async () => {
      const created = await api.post<Initiative>(
        "/initiatives",
        intakePayload(form),
      );
      const submitted = await api.post<Initiative>(
        `/initiatives/${created.publicId}/transition`,
        {
          workflowState: "submitted",
          reason: "Submitted through initiative intake",
          expectedVersion: created.version,
        },
      );
      await api.delete("/initiatives/draft");
      return submitted;
    },
    onSuccess: (initiative) => {
      localStorage.removeItem("strategic-intake-draft");
      void navigate(`/initiatives/${initiative.publicId}`);
    },
  });
  useEffect(() => {
    if (serverDraft.isPending || draftHydrated) return;
    const local = localStorage.getItem("strategic-intake-draft");
    if (!local && serverDraft.data?.draft) {
      setForm((current) => clearIntakeReviewFields({
        ...current,
        ...serverDraft.data.draft!.payload,
      }));
      setSavedAt(new Date(serverDraft.data.draft.updatedAt));
    }
    if (serverDraft.isError) setDraftStatus("error");
    setDraftHydrated(true);
  }, [
    draftHydrated,
    serverDraft.data,
    serverDraft.isError,
    serverDraft.isPending,
  ]);
  useEffect(() => {
    if (!draftHydrated || !permissionsReady) return;
    const timer = window.setTimeout(() => {
      localStorage.setItem("strategic-intake-draft", JSON.stringify(clearIntakeReviewFields(form)));
      setDraftStatus("saving");
      void api
        .put<{ savedAt: string }>("/initiatives/draft", { payload: clearIntakeReviewFields(form) })
        .then((result) => {
          setSavedAt(new Date(result.savedAt));
          setDraftStatus("idle");
        })
        .catch(() => setDraftStatus("error"));
    }, 800);
    return () => window.clearTimeout(timer);
  }, [api, draftHydrated, form, permissionsReady]);
  const set = <K extends keyof IntakeState>(key: K, value: IntakeState[K]) =>
    setForm((current) => ({ ...current, [key]: value }));
  const errors = validateIntake(form);
  const valid = Object.keys(errors).length === 0;
  const fieldError = (key: IntakeTextField) =>
    touched[key] || submitAttempted ? errors[key] : undefined;
  const touch = (key: IntakeTextField) =>
    setTouched((current) => ({ ...current, [key]: true }));
  if (permissions.isError) return <ErrorNotice error={permissions.error} />;
  if (permissions.isPending || (!draftHydrated && serverDraft.isPending))
    return <PageSkeleton variant="form" />;
  return (
    <div className="page scroll-page">
      <PageHeader
        title="Submit an initiative"
        description="Drafts autosave securely and can be resumed on another device. Final submission begins the governance SLA."
        actions={
          <span className={`autosave ${draftStatus}`} role="status">
            {draftStatus === "saving" ? (
              <ButtonLoader label="Saving draft…" />
            ) : draftStatus === "error" ? (
              <>
                <AlertTriangle size={13} /> Draft saved on this device only
              </>
            ) : (
              <>
                <Save size={13} />{" "}
                {savedAt
                  ? `Saved ${savedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
                  : "Draft ready"}
              </>
            )}
          </span>
        }
      />
      <form
        ref={formRef}
        noValidate
        className="intake-layout"
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitAttempted(true);
          if (valid) mutation.mutate();
          else
            window.requestAnimationFrame(() =>
              formRef.current
                ?.querySelector<HTMLElement>('[aria-invalid="true"]')
                ?.focus(),
            );
        }}
      >
        <div className="form-sections">
          <Card>
            <h2>Strategic need</h2>
            <div className="form-grid">
              <Field
                label="Initiative name"
                required
                span
                error={fieldError("name")}
                hint={`${form.name.length}/180 characters`}
              >
                <input
                  value={form.name}
                  onChange={(event) => set("name", event.target.value)}
                  onBlur={() => touch("name")}
                  required
                  minLength={3}
                  maxLength={180}
                  aria-invalid={Boolean(fieldError("name"))}
                />
              </Field>
              <Field
                label="Description"
                required
                span
                error={fieldError("description")}
                hint={`${form.description.length}/10,000 characters`}
              >
                <textarea
                  value={form.description}
                  onChange={(event) => set("description", event.target.value)}
                  onBlur={() => touch("description")}
                  required
                  minLength={10}
                  rows={5}
                  maxLength={10000}
                  aria-invalid={Boolean(fieldError("description"))}
                />
              </Field>
              <Field
                label="Expected outcome"
                span
                hint="Record the intended benefit or measurable result; existing outcomes are not inferred automatically."
              >
                <textarea
                  rows={3}
                  maxLength={4000}
                  value={form.expectedOutcome ?? ""}
                  onChange={(e) => set("expectedOutcome", e.target.value)}
                />
              </Field>
              <Field
                label="Strategic driver"
                required
                error={fieldError("strategicDriver")}
              >
                <input
                  value={form.strategicDriver}
                  onChange={(event) =>
                    set("strategicDriver", event.target.value)
                  }
                  onBlur={() => touch("strategicDriver")}
                  required
                  minLength={2}
                  maxLength={120}
                  aria-invalid={Boolean(fieldError("strategicDriver"))}
                />
              </Field>
              <Field label="Category" required>
                <select
                  value={form.category}
                  onChange={(event) =>
                    set(
                      "category",
                      event.target.value as IntakeState["category"],
                    )
                  }
                >
                  {["A", "B", "C", "D", "F"].map((value) => (
                    <option key={value} value={value}>
                      {value} — {categoryName(value)}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          </Card>
          <Card>
            <h2>Ownership and timing</h2>
            <div className="form-grid">
              <Field
                label="Submitting team"
                required
                error={fieldError("team")}
                hint={departmentScoped ? "Your assigned department. Contact an administrator if this needs correcting." : undefined}
              >
                {departmentScoped ? (
                  <select value={form.team} required onChange={event => set("team", event.target.value)} onBlur={() => touch("team")} aria-invalid={Boolean(fieldError("team"))}>
                    <option value="" disabled>Select your department</option>
                    {permissions.businessAccess!.departments.map(department => <option key={department} value={department}>{department}</option>)}
                  </select>
                ) : <input
                  value={form.team}
                  onChange={(event) => set("team", event.target.value)}
                  onBlur={() => touch("team")}
                  required
                  minLength={2}
                  maxLength={120}
                  aria-invalid={Boolean(fieldError("team"))}
                />}
              </Field>
              <Field
                label="Executive sponsor"
                required
                error={fieldError("sponsor")}
              >
                <input
                  value={form.sponsor}
                  onChange={(event) => set("sponsor", event.target.value)}
                  onBlur={() => touch("sponsor")}
                  required
                  minLength={2}
                  maxLength={160}
                  aria-invalid={Boolean(fieldError("sponsor"))}
                />
              </Field>
              <p>Product and technical owners will be appointed by the triage team after submission.</p>
              <Field
                label="Target quarter"
                error={fieldError("targetQuarter")}
                hint="For example, Q4 2026"
              >
                <input
                  value={form.targetQuarter}
                  onChange={(event) => set("targetQuarter", event.target.value)}
                  onBlur={() => touch("targetQuarter")}
                  placeholder="e.g. Q4 2026"
                  maxLength={40}
                  aria-invalid={Boolean(fieldError("targetQuarter"))}
                />
              </Field>
              <Field
                label="Estimated sprints"
                error={fieldError("estimatedSprints")}
                hint="Whole number from 0 to 520"
              >
                <input
                  type="number"
                  min="0"
                  max="520"
                  value={form.estimatedSprints}
                  onChange={(event) =>
                    set("estimatedSprints", event.target.value)
                  }
                  onBlur={() => touch("estimatedSprints")}
                  step="1"
                  aria-invalid={Boolean(fieldError("estimatedSprints"))}
                />
              </Field>
              <Field label="Deadline" error={fieldError("deadline")}>
                <input
                  value={form.deadline}
                  onChange={(event) => set("deadline", event.target.value)}
                  onBlur={() => touch("deadline")}
                  maxLength={80}
                  aria-invalid={Boolean(fieldError("deadline"))}
                />
              </Field>
              <Field label="Visibility" hint="Business-role access currently follows department and portfolio permissions for both options. Confidential is not participants-only for these users; do not use it to restrict sensitive information. Participant-only rules are pending confirmation.">
                <select
                  value={form.confidentiality}
                  onChange={(event) =>
                    set(
                      "confidentiality",
                      event.target.value as IntakeState["confidentiality"],
                    )
                  }
                >
                  <option value="standard">
                    Standard — users with portfolio access
                  </option>
                  <option value="confidential">
                    Confidential — access restrictions pending
                  </option>
                </select>
              </Field>
            </div>
          </Card>
        </div>
        <aside className="submit-panel">
          <Card>
            <ShieldCheck size={22} />
            <h2>Before submitting</h2>
            <ul>
              <li>Triage target: 5 business days</li>
              <li>Scoring target: 10 business days</li>
              <li>Scores are calculated by governance admins</li>
              <li>Governance risk checks are completed by authorised reviewers after submission</li>
              <li>All changes and decisions are audited</li>
            </ul>
            {submitAttempted && !valid && (
              <div className="validation-summary" role="alert">
                <AlertTriangle size={15} />
                <span>
                  Please correct the {Object.keys(errors).length} highlighted{" "}
                  {Object.keys(errors).length === 1 ? "field" : "fields"} before
                  submitting.
                </span>
              </div>
            )}
            {mutation.error && <ErrorNotice error={mutation.error} />}
            <button className="btn-primary full" disabled={mutation.isPending}>
              {mutation.isPending ? (
                <ButtonLoader label="Submitting initiative…" />
              ) : (
                <>
                  <Send size={15} /> Submit for triage
                </>
              )}
            </button>
            <button
              type="button"
              className="btn-outline full"
              onClick={() => {
                localStorage.setItem(
                  "strategic-intake-draft",
                  JSON.stringify(form),
                );
                setSavedAt(new Date());
                setDraftStatus("saving");
                void api
                  .put<{ savedAt: string }>("/initiatives/draft", {
                    payload: clearIntakeReviewFields(form),
                  })
                  .then((result) => {
                    setSavedAt(new Date(result.savedAt));
                    setDraftStatus("idle");
                  })
                  .catch(() => setDraftStatus("error"));
              }}
              disabled={draftStatus === "saving"}
            >
              {draftStatus === "saving" ? (
                <ButtonLoader label="Saving draft…" />
              ) : (
                <>
                  <Save size={15} /> Save draft
                </>
              )}
            </button>
          </Card>
        </aside>
      </form>
    </div>
  );
}

export function DependenciesPage() {
  const query = useInitiatives();
  if (query.isPending) return <PageSkeleton variant="table" />;
  if (query.isError)
    return (
      <PageError
        error={query.error}
        onRetry={() => void query.refetch()}
        retrying={query.isFetching}
      />
    );
  const byId = new Map(query.data.items.map((item) => [item.id, item]));
  const edges = query.data.items.flatMap((item) =>
    (item.dependencyIds ?? []).map((dependency) => ({
      from: dependency,
      to: item.id,
    })),
  );
  const cycles = findCycles(query.data.items);
  const chain = longestDependencyChain(query.data.items);
  return (
    <div className="page scroll-page">
      <PageHeader
        title="Dependency map"
        description="Validated upstream relationships and automated cycle detection."
      />
      <div className="kpi-bar kpi-4">
        <Kpi label="Initiatives" value={query.data.total} />
        <Kpi label="Dependencies" value={edges.length} tone="blue" />
        <Kpi
          label="Independent"
          value={
            query.data.items.filter((item) => !item.dependencyIds?.length)
              .length
          }
          tone="green"
        />
        <Kpi
          label="Cycles"
          value={cycles.length}
          tone={cycles.length ? "red" : "green"}
        />
      </div>
      <Card className="table-card">
        <h2>Longest dependency chain</h2>
        <p className="muted">
          Based on recorded relationships, not a duration-based critical path.
          Hidden initiatives are excluded.
        </p>
        {cycles.length ? (
          <p role="alert">
            Resolve the dependency cycles before calculating the chain.
          </p>
        ) : (
          <div className="dependency-chain">
            {chain.map((id, index) => (
              <span key={id}>
                {index > 0 && <span aria-hidden="true"> → </span>}
                <Link to={`/initiatives/${byId.get(id)!.publicId}`}>
                  {byId.get(id)!.name}
                </Link>
              </span>
            ))}
          </div>
        )}
        <div className="table-wrap">
          <table className="initiative-table">
            <thead>
              <tr>
                <th>Initiative</th>
                <th>Depends on</th>
                <th>Blocks</th>
                <th>Dependency type</th>
                <th>Risk if delayed</th>
              </tr>
            </thead>
            <tbody>
              {query.data.items.map((item) => (
                <tr key={item.id}>
                  <td>
                    <Link to={`/initiatives/${item.publicId}`}>
                      {item.publicId} · {item.name}
                    </Link>
                  </td>
                  <td>
                    {(item.dependencyIds ?? []).map((id) => (
                      <div key={id}>
                        <Link to={`/initiatives/${byId.get(id)?.publicId}`}>
                          {byId.get(id)?.name}
                        </Link>
                      </div>
                    ))}
                    {!item.dependencyIds?.length && "None recorded"}
                  </td>
                  <td>
                    {edges
                      .filter((edge) => edge.from === item.id)
                      .map((edge) => (
                        <div key={edge.to}>
                          <Link
                            to={`/initiatives/${byId.get(edge.to)?.publicId}`}
                          >
                            {byId.get(edge.to)?.name}
                          </Link>
                        </div>
                      ))}
                    {!edges.some((edge) => edge.from === item.id) &&
                      "None recorded"}
                  </td>
                  <td>
                    {item.dependencies
                      ?.map((edge) => edge.dependencyType)
                      .filter(Boolean)
                      .join("; ") || "Not recorded"}
                  </td>
                  <td>
                    {item.dependencies
                      ?.map((edge) => edge.risk)
                      .filter(Boolean)
                      .join("; ") || "Not recorded"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <details>
          <summary>Relationship links ({edges.length})</summary>
          <div className="dependency-list">
            {edges.map((edge) => (
              <div className="dependency-edge" key={`${edge.from}-${edge.to}`}>
                <Link to={`/initiatives/${byId.get(edge.from)?.publicId}`}>
                  {byId.get(edge.from)?.publicId}
                  <strong>{byId.get(edge.from)?.name}</strong>
                </Link>
                <span>blocks →</span>
                <Link to={`/initiatives/${byId.get(edge.to)?.publicId}`}>
                  {byId.get(edge.to)?.publicId}
                  <strong>{byId.get(edge.to)?.name}</strong>
                </Link>
              </div>
            ))}
          </div>
        </details>
      </Card>
    </div>
  );
}

export function RoadmapPage() {
  const query = useInitiatives();
  if (query.isPending) return <PageSkeleton />;
  if (query.isError)
    return (
      <PageError
        error={query.error}
        onRetry={() => void query.refetch()}
        retrying={query.isFetching}
      />
    );
  const quarters = [
    ...new Set(
      query.data.items.map((item) => normalizeQuarter(item.targetQuarter)),
    ),
  ].sort(compareQuarters);
  return (
    <div className="page scroll-page">
      <PageHeader
        title="Strategic roadmap"
        description="Portfolio grouped by target quarter; sequencing remains dependency-aware."
      />
      {query.data.items.length === 0 && (
        <Card>
          <EmptyState
            title="No initiatives to display yet"
            description="Active initiatives you have permission to view will appear here, grouped by target quarter. Archived initiatives are not shown."
          />
        </Card>
      )}
      <div className="roadmap-grid">
        {quarters.map((quarter) => (
          <Card key={quarter}>
            <div className="card-header">
              <h2>{quarter}</h2>
              <span className="count-pill">
                {
                  query.data.items.filter(
                    (item) => normalizeQuarter(item.targetQuarter) === quarter,
                  ).length
                }
              </span>
            </div>
            {query.data.items
              .filter(
                (item) => normalizeQuarter(item.targetQuarter) === quarter,
              )
              .map((item) => (
                <Link
                  className={`roadmap-item cat-line-${item.category.toLowerCase()}`}
                  key={item.id}
                  to={`/initiatives/${item.publicId}`}
                >
                  <span>{item.publicId}</span>
                  <strong>{item.name}</strong>
                  <small>
                    {label(
                      item.deliveryState ??
                        item.decisionOutcome ??
                        item.workflowState,
                    )}
                  </small>
                </Link>
              ))}
          </Card>
        ))}
      </div>
    </div>
  );
}

export function GovernancePage() {
  const query = useInitiatives();
  if (query.isPending) return <PageSkeleton />;
  if (query.isError)
    return (
      <PageError
        error={query.error}
        onRetry={() => void query.refetch()}
        retrying={query.isFetching}
      />
    );
  const board = query.data.items.filter(
    (item) => item.boardState !== "not_required",
  );
  return (
    <div className="page scroll-page">
      <PageHeader
        title="Governance"
        description="Cadence, escalation rules, and the live board portfolio."
      />
      <div className="content-grid">
        <GovernanceGuide />
        <Card>
          <div className="card-header">
            <h2>Operating cadence</h2>
          </div>
          <div className="timeline">
            {[
              [
                "Weekly",
                "Intake triage",
                "Review submissions and assign ownership",
              ],
              [
                "Bi-weekly",
                "Strategic prioritisation",
                "Score and resolve contention",
              ],
              [
                "Monthly",
                "Executive portfolio review",
                "Confirm decisions and board readiness",
              ],
              [
                "Quarterly",
                "Board portfolio review",
                "Review escalations, ageing, and dependencies",
              ],
            ].map(([when, title, copy]) => (
              <div className="timeline-row" key={title}>
                <span>{when}</span>
                <div>
                  <strong>{title}</strong>
                  <p>{copy}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
        <Card>
          <div className="card-header">
            <h2>Reviewer-approved escalation</h2>
          </div>
          <ul className="policy-list">
            <li>Regulatory or BoG approval requirement</li>
            <li>Material capital commitment</li>
            <li>Contractual exclusivity</li>
            <li>Material enterprise risk</li>
          </ul>
          <p className="notice">
            <ShieldCheck size={16} /> RICE never suppresses a governance
            escalation.
          </p>
        </Card>
        <Card className="span-2">
          <div className="card-header">
            <div>
              <h2>Board portfolio</h2>
              <p>
                {board.length} initiatives currently require or are progressing
                through board governance.
              </p>
            </div>
          </div>
          <InitiativeTable items={board} />
        </Card>
      </div>
    </div>
  );
}

export function ExecutivePage({ user }: { user: User }) {
  const api = useApi();
  const summary = useQuery({
    queryKey: ["dashboard-summary"],
    queryFn: () => api.get<DashboardSummary>("/dashboard/summary"),
  });
  const initiatives = useInitiatives();
  if (summary.isPending || initiatives.isPending) return <PageSkeleton />;
  if (summary.isError)
    return (
      <PageError
        error={summary.error}
        onRetry={() => void summary.refetch()}
        retrying={summary.isFetching}
      />
    );
  if (initiatives.isError)
    return (
      <PageError
        error={initiatives.error}
        onRetry={() => void initiatives.refetch()}
        retrying={initiatives.isFetching}
      />
    );
  return (
    <div className="page scroll-page executive-print">
      <PageHeader
        title="Monthly executive view"
        description={`Live portfolio snapshot · ${formatDate(new Date())}`}
        actions={
          <SensitiveExports>
            <ExportButton
              label="PDF"
              icon={<Printer size={14} />}
              run={() =>
                api.download(
                  "/reports/executive.pdf",
                  "strategic-executive-report.pdf",
                )
              }
            />
            <ExportButton
              label="Excel"
              icon={<FileSpreadsheet size={14} />}
              run={() =>
                api.download(
                  "/reports/initiatives.xlsx",
                  "strategic-initiatives.xlsx",
                )
              }
            />
          </SensitiveExports>
        }
      />
      <div className="kpi-bar kpi-6">
        <Kpi label="Portfolio" value={summary.data.total} />
        <Kpi
          label="Approved"
          value={summary.data.decisions.approved ?? 0}
          tone="green"
        />
        <Kpi
          label="In delivery"
          value={summary.data.delivery.in_delivery ?? 0}
          tone="blue"
        />
        <Kpi
          label="Decision pending"
          value={summary.data.workflow.decision_pending ?? 0}
          tone="amber"
        />
        <Kpi label="Board" value={summary.data.boardRequired} tone="purple" />
        <Kpi label="Stale" value={summary.data.freshness.stale} tone="red" />
      </div>
      <div className="content-grid">
        <PortfolioReview user={user} />
        <Card className="span-2">
          <div className="card-header">
            <h2>Board escalation tracker</h2>
          </div>
          <InitiativeTable
            items={initiatives.data.items.filter(
              (item) => item.boardState !== "not_required",
            )}
          />
        </Card>
        <Card className="span-2">
          <div className="card-header">
            <h2>Top priorities by quarter</h2>
          </div>
          {[
            ...new Set(
              initiatives.data.items
                .filter((item) =>
                  ["critical", "high"].includes(item.priorityBand ?? ""),
                )
                .map((item) => normalizeQuarter(item.targetQuarter)),
            ),
          ]
            .sort(compareQuarters)
            .map((quarter) => (
              <details key={quarter} open>
                <summary>{quarter}</summary>
                <InitiativeTable
                  items={initiatives.data.items.filter(
                    (item) =>
                      normalizeQuarter(item.targetQuarter) === quarter &&
                      ["critical", "high"].includes(item.priorityBand ?? ""),
                  )}
                />
              </details>
            ))}
          {!initiatives.data.items.some((item) =>
            ["critical", "high"].includes(item.priorityBand ?? ""),
          ) && <p>No high or critical priorities recorded.</p>}
        </Card>
        <Card className="span-2">
          <div className="card-header">
            <h2>BoG / regulatory approval tracker</h2>
          </div>
          <InitiativeTable
            items={initiatives.data.items.filter(
              (item) => item.regulatoryApprovalRequired,
            )}
          />
        </Card>
        <Card className="span-2">
          <div className="card-header">
            <h2>Ageing and unreviewed initiatives</h2>
          </div>
          <InitiativeTable
            items={initiatives.data.items.filter(
              (item) =>
                item.freshness.status === "stale" || !item.lastReviewedAt,
            )}
          />
        </Card>
        <Card>
          <div className="card-header">
            <h2>Decision distribution</h2>
          </div>
          <div className="metric-list">
            {Object.entries(summary.data.decisions).map(([key, value]) => (
              <div className="metric-row" key={key}>
                <span>{label(key)}</span>
                <strong>{value}</strong>
              </div>
            ))}
          </div>
        </Card>
        <Card>
          <div className="card-header">
            <h2>Delivery distribution</h2>
          </div>
          <div className="metric-list">
            {Object.entries(summary.data.delivery).map(([key, value]) => (
              <div className="metric-row" key={key}>
                <span>{label(key)}</span>
                <strong>{value}</strong>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

export function AdminAuditPage() {
  const api = useApi();
  const audit = useQuery({
    queryKey: ["audit"],
    queryFn: () =>
      api.get<{ items: Array<Record<string, unknown>> }>(
        "/admin/audit?limit=100",
      ),
  });
  const logins = useQuery({
    queryKey: ["login-history"],
    queryFn: () =>
      api.get<{ items: Array<Record<string, unknown>> }>(
        "/admin/login-history?limit=100",
      ),
  });
  const [tab, setTab] = useState<"changes" | "logins">("changes");
  if (audit.isPending || logins.isPending)
    return <PageSkeleton variant="table" />;
  if (audit.isError)
    return (
      <PageError
        error={audit.error}
        onRetry={() => void audit.refetch()}
        retrying={audit.isFetching}
      />
    );
  if (logins.isError)
    return (
      <PageError
        error={logins.error}
        onRetry={() => void logins.refetch()}
        retrying={logins.isFetching}
      />
    );
  const rows = tab === "changes" ? audit.data.items : logins.data.items;
  return (
    <div className="page scroll-page">
      <PageHeader
        title="Audit and login history"
        description="Append-only governance and security evidence with configurable retention."
        actions={
          <button
            className="btn-outline"
            onClick={() => downloadCsv(`strategic-${tab}.csv`, rows)}
          >
            <Download size={14} /> Export CSV
          </button>
        }
      />
      <div className="segmented">
        <button
          className={tab === "changes" ? "active" : ""}
          onClick={() => setTab("changes")}
        >
          Change audit
        </button>
        <button
          className={tab === "logins" ? "active" : ""}
          onClick={() => setTab("logins")}
        >
          Login history
        </button>
      </div>
      <Card className="table-card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                {tab === "changes" ? (
                  <>
                    <th>Time</th>
                    <th>Actor</th>
                    <th>Action</th>
                    <th>Entity</th>
                    <th>Reason</th>
                    <th>Integrity hash</th>
                  </>
                ) : (
                  <>
                    <th>Time</th>
                    <th>User</th>
                    <th>Outcome</th>
                    <th>IP</th>
                    <th>User agent</th>
                    <th>Correlation</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) =>
                tab === "changes" ? (
                  <tr key={String(row.id ?? index)}>
                    <td>{formatDate(String(row.occurredAt))}</td>
                    <td>{String(row.actorEmail ?? "System")}</td>
                    <td>{label(String(row.action))}</td>
                    <td>
                      {String(row.entityType)} {String(row.entityId ?? "")}
                    </td>
                    <td>{String(row.reason ?? "—")}</td>
                    <td className="hash">
                      {String(row.eventHash).slice(0, 16)}…
                    </td>
                  </tr>
                ) : (
                  <tr key={String(row.id ?? index)}>
                    <td>{formatDate(String(row.occurredAt))}</td>
                    <td>{String(row.email ?? "—")}</td>
                    <td>{label(String(row.outcome))}</td>
                    <td>{String(row.ipAddress ?? "—")}</td>
                    <td className="ua-cell">{String(row.userAgent ?? "—")}</td>
                    <td>{String(row.correlationId ?? "—")}</td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

export function AccessManagementPage({ currentUser }: { currentUser: User }) {
  const api = useApi();
  const businessPolicy = useQuery({
    queryKey: ["business-policy"],
    queryFn: () => api.get<{ policy: BusinessPolicy }>("/permissions/rollout/"),
  });
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState<AccessGrant["role"]>("user");
  const [changeReason, setChangeReason] = useState(
    "Approved application access",
  );
  const [includeArchived, setIncludeArchived] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState<AccessGrant | null>(null);
  const [archiveReason, setArchiveReason] = useState("");
  const grants = useQuery({
    queryKey: ["access-grants", includeArchived],
    queryFn: () =>
      api.get<{ items: AccessGrant[] }>(
        `/admin/access-grants?includeArchived=${includeArchived}`,
      ),
  });
  const createGrant = useMutation({
    mutationFn: (body: {
      email: string;
      displayName?: string;
      role: AccessGrant["role"];
      changeReason: string;
    }) => api.post<{ item: AccessGrant }>("/admin/access-grants", body),
    onSuccess: async () => {
      setEmail("");
      setDisplayName("");
      setRole("user");
      setChangeReason("Approved application access");
      await queryClient.invalidateQueries({ queryKey: ["access-grants"] });
    },
  });
  const updateGrant = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api.patch<{ item: AccessGrant }>(`/admin/access-grants/${id}`, body),
    onSuccess: async (_data, variables) => {
      if ("archived" in variables.body) {
        setArchiveTarget(null);
        setArchiveReason("");
      }
      await queryClient.invalidateQueries({ queryKey: ["access-grants"] });
    },
  });

  return (
    <div className="page scroll-page">
      <PageHeader
        title="User access"
        description="Only email addresses approved here can enter the Strategic Dashboard after Microsoft verifies their identity."
      />
      <div className="access-layout">
        <Card className="access-form-card">
          <div className="card-header">
            <div>
              <span className="access-step">Step 1 · Authorise</span>
              <h2>Add an authorised user</h2>
              <p>The email must match the user’s Microsoft work account.</p>
              <p>Adding an account does not send an invitation. Assign its business role in step 2.</p>
            </div>
            <span className="board-badge">Super admin only</span>
          </div>
          <form
            className="form-grid"
            onSubmit={(event) => {
              event.preventDefault();
              createGrant.mutate({
                email: email.trim().toLowerCase(),
                displayName: displayName.trim() || undefined,
                role,
                changeReason: changeReason.trim(),
              });
            }}
          >
            <label className="field">
              Work email <span aria-hidden="true">*</span>
              <input
                type="email"
                value={email}
                required
                maxLength={320}
                autoComplete="off"
                placeholder="name@payangel.com"
                onChange={(event) => setEmail(event.target.value)}
              />
            </label>
            <label className="field">
              Display name
              <input
                value={displayName}
                minLength={2}
                maxLength={200}
                placeholder="Optional until first login"
                onChange={(event) => setDisplayName(event.target.value)}
              />
            </label>
            <label className="field">
              Legacy access level <span aria-hidden="true">*</span>
              <select
                value={role}
                onChange={(event) =>
                  setRole(event.target.value as AccessGrant["role"])
                }
              >
                <option value="user">User</option>
                <option value="admin">Administrator</option>
                <option value="super_admin">Super administrator</option>
              </select>
            </label>
            <label className="field">
              Approval reason <span aria-hidden="true">*</span>
              <input
                value={changeReason}
                required
                minLength={3}
                maxLength={1000}
                onChange={(event) => setChangeReason(event.target.value)}
              />
            </label>
            <div className="span-2 access-form-actions">
              <button className="btn-primary" disabled={createGrant.isPending}>
                {createGrant.isPending ? (
                  <ButtonLoader label="Adding user…" />
                ) : (
                  "Add to access list"
                )}
              </button>
            </div>
          </form>
          {createGrant.isError && <ErrorNotice error={createGrant.error} />}
          {createGrant.isSuccess && (
            <SuccessNotice>The email address is now authorised. Next, assign a business role and department below.</SuccessNotice>
          )}
        </Card>
        <BusinessRoles />

        <Card className="table-card access-table-card">
          <div className="card-header">
            <div>
              <h2>Authorised accounts</h2>
              <p>Revocation takes effect on the next API request.</p>
              <p>Assigned business roles replace legacy privileges. Role permissions and rollout phase determine effective access.</p>
            </div>
            <div className="access-list-controls">
              <label className="archive-toggle">
                <input
                  type="checkbox"
                  checked={includeArchived}
                  onChange={(event) => setIncludeArchived(event.target.checked)}
                />
                Include archived
              </label>
              <span className="status-badge status-submitted">
                {grants.data?.items.filter((item) => item.active).length ?? 0}{" "}
                active
              </span>
            </div>
          </div>
          {grants.isPending ? (
            <SkeletonCard rows={5} />
          ) : grants.isError ? (
            <ErrorNotice error={grants.error} />
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Business roles / departments</th>
                    <th>Legacy access level</th>
                    <th>Status</th>
                    <th>Last sign-in</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {grants.data.items.map((grant) => {
                    const isSelf = grant.email === currentUser.email;
                    const assignment = businessPolicy.data?.policy.assignments.find(a => a.email === grant.email);
                    return (
                      <tr
                        key={grant.id}
                        className={
                          !grant.active || grant.archivedAt
                            ? "muted-row"
                            : undefined
                        }
                      >
                        <td>
                          <strong>
                            {grant.userDisplayName ??
                              grant.displayName ??
                              "Pending first login"}
                          </strong>
                          <small className="table-secondary">
                            {grant.email}
                          </small>
                        </td>
                        <td>
                          {businessPolicy.isPending ? "Loading roles…" : businessPolicy.isError ? "Unable to load roles" : assignment ? <>
                            <strong>{assignment.roles.map(r => businessRoleLabels[r]).join(", ")}</strong>
                            <small className="table-secondary">{assignment.departments.join(", ") || "Not department-scoped"}</small>
                          </> : <span className="access-unassigned">No business role assigned</span>}
                        </td>
                        <td>
                          <select
                            aria-label={`Access level for ${grant.email}`}
                            value={grant.role}
                            disabled={
                              isSelf ||
                              Boolean(grant.archivedAt) ||
                              updateGrant.isPending
                            }
                            onChange={(event) =>
                              updateGrant.mutate({
                                id: grant.id,
                                body: {
                                  role: event.target.value,
                                  changeReason:
                                    "Access level updated by super administrator",
                                },
                              })
                            }
                          >
                            <option value="user">User</option>
                            <option value="admin">Administrator</option>
                            <option value="super_admin">
                              Super administrator
                            </option>
                          </select>
                        </td>
                        <td>
                          <span
                            className={`status-badge ${
                              grant.archivedAt
                                ? "status-archived"
                                : grant.active
                                  ? "status-approved"
                                  : "status-declined"
                            }`}
                          >
                            {grant.archivedAt
                              ? "Archived"
                              : grant.active
                                ? "Active"
                                : "Revoked"}
                          </span>
                        </td>
                        <td>
                          {grant.lastLoginAt
                            ? formatDate(grant.lastLoginAt)
                            : "Never"}
                        </td>
                        <td>
                          <div className="access-actions">
                            {grant.archivedAt ? (
                              <button
                                className="btn-outline"
                                disabled={updateGrant.isPending}
                                onClick={() =>
                                  updateGrant.mutate({
                                    id: grant.id,
                                    body: {
                                      archived: false,
                                      changeReason:
                                        "Access record unarchived by super administrator",
                                    },
                                  })
                                }
                              >
                                Unarchive
                              </button>
                            ) : (
                              <>
                                <button
                                  className={
                                    grant.active
                                      ? "btn-danger-light"
                                      : "btn-outline"
                                  }
                                  disabled={isSelf || updateGrant.isPending}
                                  title={
                                    isSelf
                                      ? "You cannot revoke your own access"
                                      : undefined
                                  }
                                  onClick={() => {
                                    if (
                                      grant.active &&
                                      !window.confirm(
                                        `Revoke Strategic Dashboard access for ${grant.email}?`,
                                      )
                                    )
                                      return;
                                    updateGrant.mutate({
                                      id: grant.id,
                                      body: {
                                        active: !grant.active,
                                        changeReason: grant.active
                                          ? "Access revoked by super administrator"
                                          : "Access restored by super administrator",
                                      },
                                    });
                                  }}
                                >
                                  {grant.active ? "Revoke" : "Restore"}
                                </button>
                                {!grant.active && (
                                  <button
                                    className="btn-archive"
                                    disabled={updateGrant.isPending}
                                    onClick={() => {
                                      setArchiveTarget(grant);
                                      setArchiveReason(
                                        "Archived after access was revoked",
                                      );
                                    }}
                                  >
                                    Archive
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {updateGrant.isError && <ErrorNotice error={updateGrant.error} />}
        </Card>
      </div>
      {archiveTarget && (
        <div className="modal-backdrop" role="presentation">
          <section
            className="confirmation-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="archive-dialog-title"
          >
            <h2 id="archive-dialog-title">Archive access record?</h2>
            <p>
              <strong>{archiveTarget.email}</strong> will be hidden from the
              normal access list. The audit history will be retained and the
              record can be unarchived later.
            </p>
            <label className="field">
              Reason <span aria-hidden="true">*</span>
              <textarea
                value={archiveReason}
                required
                minLength={3}
                maxLength={1000}
                autoFocus
                onChange={(event) => setArchiveReason(event.target.value)}
              />
            </label>
            <div className="dialog-actions">
              <button
                className="btn-outline"
                disabled={updateGrant.isPending}
                onClick={() => {
                  setArchiveTarget(null);
                  setArchiveReason("");
                }}
              >
                Cancel
              </button>
              <button
                className="btn-archive"
                disabled={
                  updateGrant.isPending || archiveReason.trim().length < 3
                }
                onClick={() =>
                  updateGrant.mutate({
                    id: archiveTarget.id,
                    body: {
                      archived: true,
                      changeReason: archiveReason.trim(),
                    },
                  })
                }
              >
                {updateGrant.isPending ? (
                  <ButtonLoader label="Archiving…" />
                ) : (
                  "Archive record"
                )}
              </button>
            </div>
            {updateGrant.isError && <ErrorNotice error={updateGrant.error} />}
          </section>
        </div>
      )}
    </div>
  );
}

function RoleSettingsLink() {
  const permissions = usePermissions();
  return permissions.can("roles.manage") ? (
    <Card>
      <h2>Roles & permissions</h2>
      <p>
        Configure team summaries, individual details, exports, integrations and
        access administration for legacy accounts. Rollout assignments override legacy roles.
      </p>
      <Link className="btn-outline inline" to="/admin/access">Assign rollout roles and departments</Link>
      <Link className="btn-outline inline" to="/settings/permissions">
        Manage permissions
      </Link>
    </Card>
  ) : null;
}
export function SettingsPage({ user }: { user: User }) {
  const api = useApi();
  const delivery = useQuery({
    queryKey: ["notification-features"],
    queryFn: () => api.get<{ features: { emailNotifications: boolean } }>("/auth/me"),
  });
  const permissions = usePermissions();
  const managesSystem = permissions.businessCan("system.manage", user.role === "admin");
  const settings = useQuery({
    queryKey: ["settings"],
    enabled: managesSystem,
    queryFn: () =>
      api.get<{
        items: Array<{ key: string; value: unknown; updatedAt: string }>;
      }>("/admin/settings"),
  });
  return (
    <div className="page scroll-page">
      <PageHeader
        title="Settings"
        description="Security, notification preferences, and governance configuration."
      />
      <div className="settings-grid">
        <RolloutControls />
        <RoleSettingsLink />
        <Card>
          <ShieldCheck size={22} />
          <h2>Microsoft account security</h2>
          <p>
            MFA and passkeys are managed by Microsoft Entra ID and enforced
            through Conditional Access.
          </p>
          <a
            className="btn-primary inline"
            href="https://mysignins.microsoft.com/security-info"
            target="_blank"
            rel="noreferrer"
          >
            Manage security methods
          </a>
        </Card>
        <Card>
          <h2>Signed-in account</h2>
          <dl className="definition-list">
            <div>
              <dt>Name</dt>
              <dd>{user.displayName}</dd>
            </div>
            <div>
              <dt>Email</dt>
              <dd>{user.email}</dd>
            </div>
            <div>
              <dt>Application access</dt>
              <dd>
                {user.isSuperAdmin ? "Super administrator" : label(user.role)}
              </dd>
            </div>
          </dl>
        </Card>
        <Card>
          <h2>Notification delivery</h2>
          <p>
            In-app notifications remain available. Invitation emails are queued
            only after an administrator assigns a business role. Delivery status
            is available under User access.
          </p>
          <span className="status-badge status-submitted">
            {delivery.isPending ? "Checking email configuration…" : delivery.isError ? "Email configuration unavailable" : delivery.data.features.emailNotifications ? "Mandrill invitation delivery enabled" : "Email delivery disabled"}
          </span>
        </Card>
        {managesSystem && (
          <Card className="span-2">
            <div className="card-header">
              <h2>Governance configuration</h2>
              <span className="board-badge">Admin only</span>
            </div>
            {settings.isPending ? (
              <SkeletonCard rows={4} />
            ) : settings.isError ? (
              <ErrorNotice error={settings.error} />
            ) : (
              <div className="settings-list">
                {settings.data.items.map((setting) => (
                  <div key={setting.key}>
                    <strong>{setting.key}</strong>
                    <code>{JSON.stringify(setting.value)}</code>
                    <small>Updated {formatDate(setting.updatedAt)}</small>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}

function ScoreCard({ item }: { item: Initiative }) {
  return (
    <div className="score-card">
      <span>Adjusted RICE</span>
      <strong>{item.adjustedRice?.toFixed(1) ?? "—"}</strong>
      <small>
        {item.baseRice == null
          ? "Awaiting scoring"
          : `Base ${item.baseRice.toFixed(1)} × ${item.strategicMultiplier}`}
      </small>
      {item.priorityOverrideBand && (
        <em>Override: {label(item.priorityOverrideBand)}</em>
      )}
    </div>
  );
}

function InitiativeEditForm({
  item,
  pending,
  error,
  onSubmit,
}: {
  item: Initiative;
  pending: boolean;
  error: Error | null;
  onSubmit(body: Record<string, unknown>): void;
}) {
  const permissions = usePermissions();
  const canAssignOwners = permissions.businessCan("decide", permissions.can("roles.manage")) && permissions.businessAccess?.scope !== "department";
  const [form, setForm] = useState({
    name: item.name,
    description: item.description,
    expectedOutcome: item.expectedOutcome ?? "",
    strategicDriver: item.strategicDriver,
    team: item.team,
    sponsor: item.sponsor,
    productOwner: item.productOwner ?? "",
    technicalOwner: item.technicalOwner ?? "",
    targetQuarter: item.targetQuarter ?? "",
    deadline: item.deadline ?? "",
    estimatedSprints: String(item.estimatedSprints ?? ""),
    category: item.category,
    confidentiality: item.confidentiality,
  });
  const [reason, setReason] = useState("");
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const set = (key: keyof typeof form, value: string) =>
    setForm((current) => ({ ...current, [key]: value }));
  const editErrors = validateIntake({ ...emptyIntake, ...form });
  const reasonError =
    reason.trim().length < 3
      ? "Provide a reason of at least 3 characters."
      : undefined;
  const valid = Object.keys(editErrors).length === 0 && !reasonError;
  const show = (key: IntakeTextField) =>
    submitAttempted ? editErrors[key] : undefined;
  useEffect(() => {
    setReason("");
    setSubmitAttempted(false);
  }, [item.version]);
  return (
    <Card>
      <details>
        <summary className="edit-summary">Edit initiative details</summary>
        <form
          noValidate
          className="form-grid edit-form"
          onSubmit={(event) => {
            event.preventDefault();
            setSubmitAttempted(true);
            if (!valid) return;
            onSubmit({
              ...form,
              productOwner: canAssignOwners ? form.productOwner || null : undefined,
              technicalOwner: canAssignOwners ? form.technicalOwner || null : undefined,
              targetQuarter: form.targetQuarter || null,
              deadline: form.deadline || null,
              estimatedSprints: form.estimatedSprints
                ? Number(form.estimatedSprints)
                : null,
              changeReason: reason,
              expectedVersion: item.version,
            });
          }}
        >
          <Field label="Name" required span error={show("name")}>
            <input
              value={form.name}
              minLength={3}
              maxLength={180}
              onChange={(event) => set("name", event.target.value)}
              required
              aria-invalid={Boolean(show("name"))}
            />
          </Field>
          <Field label="Description" required span error={show("description")}>
            <textarea
              rows={4}
              minLength={10}
              value={form.description}
              onChange={(event) => set("description", event.target.value)}
              required
              maxLength={10000}
              aria-invalid={Boolean(show("description"))}
            />
          </Field>
          <Field label="Expected outcome" span>
            <textarea
              rows={3}
              maxLength={4000}
              value={form.expectedOutcome}
              onChange={(e) => set("expectedOutcome", e.target.value)}
            />
          </Field>
          <Field
            label="Strategic driver"
            required
            error={show("strategicDriver")}
          >
            <input
              value={form.strategicDriver}
              onChange={(event) => set("strategicDriver", event.target.value)}
              required
              minLength={2}
              maxLength={120}
              aria-invalid={Boolean(show("strategicDriver"))}
            />
          </Field>
          <Field label="Category">
            <select
              value={form.category}
              onChange={(event) => set("category", event.target.value)}
            >
              {["A", "B", "C", "D", "F"].map((value) => (
                <option key={value}>{value}</option>
              ))}
            </select>
          </Field>
          <Field label="Team" required error={show("team")}>
            <input
              value={form.team}
              onChange={(event) => set("team", event.target.value)}
              required
              minLength={2}
              maxLength={120}
              aria-invalid={Boolean(show("team"))}
            />
          </Field>
          <Field label="Sponsor" required error={show("sponsor")}>
            <input
              value={form.sponsor}
              onChange={(event) => set("sponsor", event.target.value)}
              required
              minLength={2}
              maxLength={160}
              aria-invalid={Boolean(show("sponsor"))}
            />
          </Field>
          {canAssignOwners ? <><Field label="Product owner" error={show("productOwner")}>
            <input
              value={form.productOwner}
              onChange={(event) => set("productOwner", event.target.value)}
              maxLength={160}
              aria-invalid={Boolean(show("productOwner"))}
            />
          </Field>
          <Field label="Technical owner" error={show("technicalOwner")}>
            <input
              value={form.technicalOwner}
              onChange={(event) => set("technicalOwner", event.target.value)}
              maxLength={160}
              aria-invalid={Boolean(show("technicalOwner"))}
            />
          </Field>
          </> : <p>Product and technical owners are managed by the triage team.</p>}
          <Field label="Target quarter" error={show("targetQuarter")}>
            <input
              value={form.targetQuarter}
              onChange={(event) => set("targetQuarter", event.target.value)}
              maxLength={40}
              aria-invalid={Boolean(show("targetQuarter"))}
            />
          </Field>
          <Field label="Deadline" error={show("deadline")}>
            <input
              value={form.deadline}
              onChange={(event) => set("deadline", event.target.value)}
              maxLength={80}
              aria-invalid={Boolean(show("deadline"))}
            />
          </Field>
          <Field label="Estimated sprints" error={show("estimatedSprints")}>
            <input
              type="number"
              min="0"
              max="520"
              value={form.estimatedSprints}
              onChange={(event) => set("estimatedSprints", event.target.value)}
              step="1"
              aria-invalid={Boolean(show("estimatedSprints"))}
            />
          </Field>
          <Field label="Visibility">
            <select
              value={form.confidentiality}
              onChange={(event) => set("confidentiality", event.target.value)}
            >
              <option value="standard">Standard</option>
              <option value="confidential">Confidential</option>
            </select>
          </Field>
          <Field
            label="Change reason"
            required
            span
            error={submitAttempted ? reasonError : undefined}
          >
            <textarea
              rows={2}
              minLength={3}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              required
              maxLength={1000}
              aria-invalid={Boolean(submitAttempted && reasonError)}
            />
          </Field>
          {error && (
            <div className="span-2">
              <ErrorNotice error={error} />
            </div>
          )}
          {submitAttempted && !valid && (
            <div className="validation-summary span-2" role="alert">
              <AlertTriangle size={15} />
              <span>Correct the highlighted fields before saving.</span>
            </div>
          )}
          <button className="btn-primary" disabled={pending}>
            {pending ? (
              <ButtonLoader label="Saving changes…" />
            ) : (
              <>
                <Save size={14} /> Save changes
              </>
            )}
          </button>
        </form>
      </details>
    </Card>
  );
}

function ScoringForm({
  item,
  pending,
  error,
  onSubmit,
}: {
  item: Initiative;
  pending: boolean;
  error: Error | null;
  onSubmit(body: Record<string, unknown>): void;
}) {
  const [reach, setReach] = useState(String(item.reach ?? 3));
  const [impact, setImpact] = useState(String(item.impact ?? 3));
  const [confidence, setConfidence] = useState(String(item.confidence ?? 0.8));
  const [effort, setEffort] = useState(String(item.effort ?? 3));
  const [multiplier, setMultiplier] = useState(
    String(item.strategicMultiplier ?? 1),
  );
  const [override, setOverride] = useState(
    item.priorityOverrideBand ?? "automatic",
  );
  const [overrideReason, setOverrideReason] = useState(
    item.priorityOverrideReason ?? "",
  );
  const [reason, setReason] = useState("");
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const reasonError =
    reason.trim().length < 3
      ? "Explain this scoring change in at least 3 characters."
      : undefined;
  const overrideError =
    override !== "automatic" && overrideReason.trim().length < 10
      ? "Priority overrides require a justification of at least 10 characters."
      : undefined;
  const basePreview =
    Math.round(
      ((Number(reach) * Number(impact) * Number(confidence)) / Number(effort)) *
        10,
    ) / 10;
  const adjustedPreview =
    Math.round(basePreview * Number(multiplier) * 10) / 10;
  useEffect(() => {
    setReach(String(item.reach ?? 3));
    setImpact(String(item.impact ?? 3));
    setConfidence(String(item.confidence ?? 0.8));
    setEffort(String(item.effort ?? 3));
    setMultiplier(String(item.strategicMultiplier ?? 1));
    setOverride(item.priorityOverrideBand ?? "automatic");
    setOverrideReason(item.priorityOverrideReason ?? "");
    setReason("");
    setSubmitAttempted(false);
  }, [item.version]);
  return (
    <Card className="lifecycle-card">
      <h2>
        <Calculator size={16} /> Scoring
      </h2>
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitAttempted(true);
          if (reasonError || overrideError) return;
          onSubmit({
            score: {
              reach: Number(reach),
              impact: Number(impact),
              confidence: Number(confidence),
              effort: Number(effort),
              strategicMultiplier: Number(multiplier),
            },
            priorityOverride:
              override === "automatic"
                ? null
                : { band: override, reason: overrideReason },
            changeReason: reason,
            expectedVersion: item.version,
          });
        }}
      >
        <div className="score-input-grid">
          <label>
            Reach
            <select
              value={reach}
              onChange={(event) => setReach(event.target.value)}
            >
              {[1, 2, 3, 4, 5].map((value) => (
                <option key={value}>{value}</option>
              ))}
            </select>
          </label>
          <label>
            Impact
            <select
              value={impact}
              onChange={(event) => setImpact(event.target.value)}
            >
              {[1, 2, 3, 4, 5].map((value) => (
                <option key={value}>{value}</option>
              ))}
            </select>
          </label>
          <label>
            Confidence
            <select
              value={confidence}
              onChange={(event) => setConfidence(event.target.value)}
            >
              {[
                ["0.2", "20%"],
                ["0.5", "50%"],
                ["0.8", "80%"],
                ["1", "100%"],
              ].map(([value, text]) => (
                <option value={value} key={value}>
                  {text}
                </option>
              ))}
            </select>
          </label>
          <label>
            Effort
            <select
              value={effort}
              onChange={(event) => setEffort(event.target.value)}
            >
              {[1, 2, 3, 4, 5].map((value) => (
                <option key={value}>{value}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="score-preview" role="status">
          <span>Calculated preview</span>
          <strong>{adjustedPreview.toFixed(1)}</strong>
          <small>
            Base {basePreview.toFixed(1)} × {multiplier}
          </small>
        </div>
        <label>
          Strategic multiplier
          <select
            value={multiplier}
            onChange={(event) => setMultiplier(event.target.value)}
          >
            {[1, 1.25, 1.75, 2, 3].map((value) => (
              <option key={value} value={value}>
                {value}×
              </option>
            ))}
          </select>
        </label>
        <label>
          Priority band
          <select
            value={override}
            onChange={(event) => setOverride(event.target.value)}
          >
            <option value="automatic">Automatic from score</option>
            {["critical", "high", "medium", "low"].map((value) => (
              <option key={value} value={value}>
                {label(value)}
              </option>
            ))}
          </select>
        </label>
        {override !== "automatic" && (
          <label>
            Override justification
            <textarea
              rows={3}
              minLength={10}
              required
              value={overrideReason}
              onChange={(event) => setOverrideReason(event.target.value)}
              maxLength={1000}
              aria-invalid={Boolean(submitAttempted && overrideError)}
            />
            <FieldError>
              {submitAttempted ? overrideError : undefined}
            </FieldError>
          </label>
        )}
        <label>
          Change reason
          <textarea
            rows={3}
            minLength={3}
            required
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            maxLength={1000}
            aria-invalid={Boolean(submitAttempted && reasonError)}
          />
          <FieldError>{submitAttempted ? reasonError : undefined}</FieldError>
        </label>
        {error && <ErrorNotice error={error} />}
        <button className="btn-primary full" disabled={pending}>
          {pending ? (
            <ButtonLoader label="Calculating and saving…" />
          ) : (
            <>
              <Calculator size={14} /> Calculate and save
            </>
          )}
        </button>
      </form>
    </Card>
  );
}

function ParticipantSection({ initiative }: { initiative: Initiative }) {
  const api = useApi();
  const query = useQuery({
    queryKey: ["participants", initiative.publicId],
    queryFn: () =>
      api.get<{
        items: Array<{ id: string; email: string; displayName: string }>;
        candidates: Array<{ id: string; email: string; displayName: string }>;
      }>(`/initiatives/${initiative.publicId}/participants`),
  });
  const [selected, setSelected] = useState<string[]>([]);
  const [reason, setReason] = useState("");
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const reasonError =
    reason.trim().length < 3
      ? "Explain why participant access is changing."
      : undefined;
  useEffect(() => {
    if (query.data)
      setSelected(query.data.items.map((participant) => participant.id));
  }, [query.data]);
  const update = useMutation({
    mutationFn: () =>
      api.put(`/initiatives/${initiative.publicId}/participants`, {
        userIds: selected,
        reason,
      }),
    onSuccess: () => {
      setReason("");
      setSubmitAttempted(false);
      void query.refetch();
    },
  });
  return (
    <Card>
      <div className="card-header">
        <div>
          <h2>
            <Users size={16} /> Named participants
          </h2>
          <p>
            Participants can view confidential initiatives. Administrators
            always retain access.
          </p>
        </div>
      </div>
      {query.isPending ? (
        <SkeletonCard rows={3} />
      ) : query.isError ? (
        <ErrorNotice error={query.error} />
      ) : (
        <form
          className="participant-form"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            setSubmitAttempted(true);
            if (reasonError) return;
            update.mutate();
          }}
        >
          <div className="participant-list">
            {query.data.candidates.map((candidate) => (
              <label className="check-card" key={candidate.id}>
                <input
                  type="checkbox"
                  checked={selected.includes(candidate.id)}
                  onChange={(event) =>
                    setSelected((current) =>
                      event.target.checked
                        ? [...current, candidate.id]
                        : current.filter(
                            (candidateId) => candidateId !== candidate.id,
                          ),
                    )
                  }
                />
                <span>
                  <strong>{candidate.displayName}</strong>
                  <small>{candidate.email}</small>
                </span>
              </label>
            ))}
          </div>
          <label>
            Reason for access change
            <textarea
              rows={2}
              minLength={3}
              required
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              maxLength={1000}
              aria-invalid={Boolean(submitAttempted && reasonError)}
            />
            <FieldError>{submitAttempted ? reasonError : undefined}</FieldError>
          </label>
          {update.error && <ErrorNotice error={update.error} />}
          {update.isSuccess && (
            <SuccessNotice>
              Participant access was updated and audited.
            </SuccessNotice>
          )}
          <button className="btn-primary" disabled={update.isPending}>
            {update.isPending ? (
              <ButtonLoader label="Saving access…" />
            ) : (
              "Save participants"
            )}
          </button>
        </form>
      )}
    </Card>
  );
}

function AttachmentSection({ initiative }: { initiative: Initiative }) {
  const api = useApi();
  const permissions = usePermissions();
  const [fileError, setFileError] = useState<string | null>(null);
  const query = useQuery({
    queryKey: ["attachments", initiative.publicId],
    queryFn: () =>
      api.get<{
        uploadsEnabled: boolean;
        items: Array<{
          id: string;
          originalName: string;
          sizeBytes: number;
          scanStatus: string;
          createdAt: string;
        }>;
      }>(`/initiatives/${initiative.publicId}/attachments`),
  });
  const upload = useMutation({
    mutationFn: (file: File) =>
      api.upload(`/initiatives/${initiative.publicId}/attachments`, file),
    onSuccess: () => {
      setFileError(null);
      void query.refetch();
    },
  });
  return (
    <Card>
      <div className="card-header">
        <div>
          <h2>Attachments</h2>
          <p>Private files are quarantined until malware scanning passes.</p>
        </div>
        {query.data?.uploadsEnabled && permissions.businessCan("edit",true) && (
          <label
            className={`btn-outline file-button ${upload.isPending ? "is-disabled" : ""}`}
          >
            {upload.isPending ? <ButtonLoader label="Uploading…" /> : "Upload"}
            <input
              type="file"
              disabled={upload.isPending}
              accept=".pdf,.docx,.xlsx,.csv,.png,.jpg,.jpeg"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) {
                  const extension = file.name.split(".").pop()?.toLowerCase();
                  if (
                    !extension ||
                    ![
                      "pdf",
                      "docx",
                      "xlsx",
                      "csv",
                      "png",
                      "jpg",
                      "jpeg",
                    ].includes(extension)
                  )
                    setFileError(
                      "Choose a PDF, DOCX, XLSX, CSV, PNG, or JPEG file.",
                    );
                  else if (file.size > 25 * 1024 * 1024)
                    setFileError(
                      "The selected file is larger than the 25 MB limit.",
                    );
                  else {
                    setFileError(null);
                    upload.mutate(file);
                  }
                }
                event.target.value = "";
              }}
            />
          </label>
        )}
      </div>
      {query.isPending ? (
        <SkeletonCard rows={3} />
      ) : query.isError ? (
        <ErrorNotice error={query.error} />
      ) : (
        <>
          {!query.data.uploadsEnabled && (
            <p className="notice">
              Attachment uploads will activate when the Azure Storage account
              and managed identity are configured.
            </p>
          )}
          {query.data.items.length === 0 ? (
            <EmptyState
              title="No attachments"
              description="Supporting documents will appear here after upload and malware scanning."
            />
          ) : (
            <div className="attachment-list">
              {query.data.items.map((item) => (
                <div key={item.id}>
                  <div>
                    <strong>{item.originalName}</strong>
                    <small>
                      {(item.sizeBytes / 1024).toFixed(1)} KB ·{" "}
                      {formatDate(item.createdAt)}
                    </small>
                  </div>
                  <span className={`status-badge status-${item.scanStatus}`}>
                    {label(item.scanStatus)}
                  </span>
                  {item.scanStatus === "clean" && (
                    <ExportButton
                      label="Download"
                      icon={<Download size={14} />}
                      run={() =>
                        api.download(
                          `/initiatives/${initiative.publicId}/attachments/${item.id}/download`,
                          item.originalName,
                        )
                      }
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
      {fileError && (
        <div className="validation-summary" role="alert">
          <AlertTriangle size={15} />
          <span>{fileError}</span>
        </div>
      )}
      {upload.error && <ErrorNotice error={upload.error} />}
    </Card>
  );
}

function ReviewerChecks({
  item,
  pending,
  error,
  onSubmit,
}: {
  item: Initiative;
  pending: boolean;
  error: Error | null;
  onSubmit(body: Record<string, unknown>): void;
}) {
  const keys = [
    "regulatoryApprovalRequired",
    "materialCapitalCommitment",
    "contractualExclusivity",
    "materialEnterpriseRisk",
  ] as const;
  const labels = [
    "Regulatory / BoG approval",
    "Material capital commitment",
    "Contractual exclusivity",
    "Material enterprise risk",
  ];
  const [checks, setChecks] = useState(
    Object.fromEntries(keys.map((key) => [key, Boolean(item[key])])),
  );
  const [reason, setReason] = useState("");
  return (
    <Card>
      <h2>Governance review checks</h2>
      <p className="section-help">
        Reviewer-only inputs. Saving does not escalate or notify anyone and
        requires renewed approval before sending an escalation.
      </p>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit({
            boardTriggers: checks,
            expectedVersion: item.version,
            changeReason: reason,
          });
        }}
      >
        <div className="check-grid">
          {keys.map((key, index) => (
            <label key={key}>
              <input
                type="checkbox"
                checked={checks[key]}
                onChange={(event) =>
                  setChecks({ ...checks, [key]: event.target.checked })
                }
              />
              {labels[index]}
            </label>
          ))}
        </div>
        <label>
          Review reason
          <textarea
            required
            minLength={3}
            maxLength={1000}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </label>
        {error && <ErrorNotice error={error} />}
        <button
          className="btn-outline"
          disabled={pending || reason.trim().length < 3}
        >
          {pending ? (
            <ButtonLoader label="Saving checks…" />
          ) : (
            "Save review checks"
          )}
        </button>
      </form>
    </Card>
  );
}

function LifecycleForm({
  administrator,
  item,
  pending,
  error,
  onSubmit,
}: {
  administrator: boolean;
  item: Initiative;
  pending: boolean;
  error: Error | null;
  onSubmit(body: Record<string, unknown>): void;
}) {
  const permissions = usePermissions();
  const canDecide = permissions.businessCan("decide", administrator);
  const canSubmit = item.workflowState === "draft" && permissions.businessCan("submit");
  const canDeliver = permissions.businessCan("delivery.update", administrator);
  const [dimension, setDimension] = useState(canDecide || canSubmit ? "workflowState" : canDeliver ? "deliveryState" : "boardState");
  const [value, setValue] = useState("submitted");
  const [reason, setReason] = useState("");
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const reasonError =
    reason.trim().length < 3
      ? "Provide a reason of at least 3 characters for the audit trail."
      : undefined;
  const options: Record<string, string[]> = {
    workflowState: canDecide ? ["submitted", "triage", "scoring", "decision_pending"] : ["submitted"],
    decisionOutcome: ["approved", "queued", "parked", "declined"],
    deliveryState: ["planned", "in_delivery", "paused", "completed", "closed"],
    boardState: ["required", "submitted", "decided", "withdrawn"],
  };
  useEffect(() => setValue(options[dimension]?.[0] ?? ""), [dimension]);
  useEffect(() => {
    setReason("");
    setSubmitAttempted(false);
  }, [item.version]);
  return (
    <Card className="lifecycle-card">
      <h2>Lifecycle action</h2>
      <p className="section-help">
        An authorised role records each workflow, decision, delivery, or board
        update with a reason. These states are not automatically imported from
        Linear. The change is audited and recorded in the initiative history.
        Escalations require approval of the current version before an explicit
        in-app send; edits require a fresh review. No email is sent.
      </p>
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitAttempted(true);
          if (reasonError) return;
          onSubmit({
            [dimension]: value,
            reason,
            expectedVersion: item.version,
          });
        }}
      >
        <label>
          Dimension
          <select
            value={dimension}
            onChange={(event) => setDimension(event.target.value)}
          >
            {(canDecide || canSubmit) && <option value="workflowState">Workflow</option>}
            {canDecide && <option value="decisionOutcome">Decision</option>}
            {canDeliver && <option value="deliveryState">Delivery</option>}
            {permissions.can("governance.manage") && (
              <option value="boardState">
                Board escalation (review required)
              </option>
            )}
          </select>
        </label>
        <label>
          New state
          <select
            value={value}
            onChange={(event) => setValue(event.target.value)}
          >
            {options[dimension]?.map((option) => (
              <option key={option} value={option}>
                {dimension === "boardState" && option === "required"
                  ? "Approve escalation after review"
                  : dimension === "boardState" && option === "submitted"
                    ? "Send approved escalation (in-app)"
                    : label(option)}
              </option>
            ))}
          </select>
        </label>
        <label>
          Reason
          <textarea
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            rows={3}
            required
            minLength={3}
            maxLength={2000}
            aria-invalid={Boolean(submitAttempted && reasonError)}
          />
          <FieldError>{submitAttempted ? reasonError : undefined}</FieldError>
        </label>
        {error && <ErrorNotice error={error} />}
        <button className="btn-primary full" disabled={pending}>
          {pending ? (
            <ButtonLoader label="Applying transition…" />
          ) : (
            <>
              <CheckCircle2 size={14} /> Apply transition
            </>
          )}
        </button>
      </form>
    </Card>
  );
}

function ExportButton({
  label: text,
  icon,
  run,
}: {
  label: string;
  icon: React.ReactNode;
  run(): Promise<void>;
}) {
  const [state, setState] = useState<"idle" | "loading" | "error">("idle");
  return (
    <button
      className={`btn-outline ${state === "error" ? "button-error" : ""}`}
      disabled={state === "loading"}
      title={
        state === "error" ? "Export failed. Select to try again." : undefined
      }
      onClick={() => {
        setState("loading");
        void run()
          .then(() => setState("idle"))
          .catch(() => setState("error"));
      }}
    >
      {state === "loading" ? (
        <ButtonLoader label={`Preparing ${text}…`} />
      ) : state === "error" ? (
        <>
          <AlertTriangle size={14} /> Retry {text}
        </>
      ) : (
        <>
          {icon} {text}
        </>
      )}
    </button>
  );
}

function Field({
  label: text,
  required,
  span,
  error,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  span?: boolean;
  error?: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={`field ${span ? "span-2" : ""}`}>
      <span>
        {text}
        {required && <b aria-hidden="true"> *</b>}
      </span>
      {children}
      {hint && !error && <small className="field-hint">{hint}</small>}
      <FieldError>{error}</FieldError>
    </label>
  );
}

function intakePayload(form: IntakeState) {
  const checks = clearIntakeReviewFields(form);
  return {
    name: form.name.trim(),
    description: form.description.trim(),
    expectedOutcome: form.expectedOutcome?.trim() || null,
    category: form.category,
    strategicDriver: form.strategicDriver.trim(),
    team: form.team.trim(),
    sponsor: form.sponsor.trim(),
    productOwner: null,
    technicalOwner: null,
    targetQuarter: form.targetQuarter.trim() || null,
    deadline: form.deadline.trim() || null,
    estimatedSprints: form.estimatedSprints
      ? Number(form.estimatedSprints)
      : null,
    confidentiality: form.confidentiality,
    score: null,
    boardTriggers: {
      regulatoryApprovalRequired: checks.regulatoryApprovalRequired,
      materialCapitalCommitment: checks.materialCapitalCommitment,
      contractualExclusivity: checks.contractualExclusivity,
      materialEnterpriseRisk: checks.materialEnterpriseRisk,
    },
    dependencyIds: [],
  };
}

function DonutLike({
  rows,
  total,
}: {
  rows: Array<[string, number, string]>;
  total: number;
}) {
  return (
    <div className="health-summary">
      <div className="health-total">
        <strong>{total}</strong>
        <span>Total</span>
      </div>
      <div className="metric-list">
        {rows.map(([name, value, tone]) => (
          <div className="metric-row" key={name}>
            <span>
              <i className={`tone-dot ${tone}`} />
              {name}
            </span>
            <strong>{value}</strong>
          </div>
        ))}
      </div>
    </div>
  );
}

function categoryName(category: string) {
  return (
    (
      {
        A: "Regulatory mandates",
        B: "Partner commitments",
        C: "Strategic growth",
        D: "Platform foundations",
        F: "Exploratory",
      } as Record<string, string>
    )[category] ?? category
  );
}

function findCycles(items: Initiative[]) {
  const graph = new Map(
    items.map((item) => [item.id, item.dependencyIds ?? []]),
  );
  const cycles: number[][] = [];
  const visiting = new Set<number>();
  const visited = new Set<number>();
  const walk = (node: number, path: number[]) => {
    if (visiting.has(node)) {
      cycles.push([...path.slice(path.indexOf(node)), node]);
      return;
    }
    if (visited.has(node)) return;
    visiting.add(node);
    for (const next of graph.get(node) ?? []) walk(next, [...path, node]);
    visiting.delete(node);
    visited.add(node);
  };
  for (const node of graph.keys()) walk(node, []);
  return cycles;
}
